/*
 * <rv-flip> — two-sided flip viewer for reversible products (plus <rv-video>, below).
 * Used by sections/reversible-flip-hero.liquid and sections/reversible-product-grid.liquid;
 * <rv-video> also by sections/reversible-product-showcase.liquid.
 *
 * Markup contract (all optional except the stage):
 *   <rv-flip data-side="a" [data-autohint] [data-hover-flip]>
 *     <div data-rv-stage> …two faces… </div>
 *     <button data-rv-toggle>          flips to the other side
 *     <button data-rv-show="a|b">      shows a specific side (aria-pressed kept in sync)
 *     <span data-rv-label data-label-a="…" data-label-b="…">   text swaps with the side
 *   </rv-flip>
 *
 * Interaction: tap/click the stage, swipe left/right on the stage, arrow keys when focused,
 * optional hover on fine pointers, and a one-time hint flip when first scrolled into view.
 * Honours prefers-reduced-motion (CSS crossfades instead of rotating; no auto hint).
 */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('rv-flip')) {
    class RvFlip extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('[data-rv-stage]');
        if (!this.stage || this._ready) return;
        this._ready = true;
        this.interacted = false;
        this.setSide(this.dataset.side === 'b' ? 'b' : 'a', false);

        this.stage.addEventListener('click', (event) => {
          if (this._swiped) {
            this._swiped = false;
            return;
          }
          event.preventDefault();
          this.flip();
        });

        this.stage.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.flip();
          }
          if (event.key === 'ArrowLeft') this.setSide('a');
          if (event.key === 'ArrowRight') this.setSide('b');
        });

        this.stage.addEventListener('pointerdown', (event) => {
          this._x = event.clientX;
          this._y = event.clientY;
        });

        this.stage.addEventListener('pointerup', (event) => {
          if (this._x == null) return;
          const dx = event.clientX - this._x;
          const dy = event.clientY - this._y;
          this._x = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            this._swiped = true;
            this.flip();
          }
        });

        this.querySelectorAll('[data-rv-toggle]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.flip();
          });
        });

        this.querySelectorAll('[data-rv-show]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.setSide(button.dataset.rvShow);
          });
        });

        if (this.hasAttribute('data-hover-flip') && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.stage.addEventListener('mouseenter', () => this.setSide('b', false));
          this.stage.addEventListener('mouseleave', () => this.setSide('a', false));
        }

        if (this.hasAttribute('data-autohint') && !reducedMotion() && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              observer.disconnect();
              setTimeout(() => {
                if (this.interacted) return;
                this.setSide('b', false);
                setTimeout(() => {
                  if (!this.interacted) this.setSide('a', false);
                }, 1600);
              }, 1200);
            },
            { threshold: 0.6 }
          );
          observer.observe(this.stage);
        }
      }

      flip() {
        this.setSide(this.dataset.side === 'b' ? 'a' : 'b');
      }

      setSide(side, fromUser = true) {
        if (side !== 'a' && side !== 'b') return;
        if (fromUser) this.interacted = true;
        this.dataset.side = side;

        this.querySelectorAll('[data-rv-show]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.rvShow === side));
        });

        this.querySelectorAll('[data-rv-label]').forEach((label) => {
          const text = side === 'b' ? label.dataset.labelB : label.dataset.labelA;
          if (text) label.textContent = text;
        });

        this.querySelectorAll('[data-rv-face]').forEach((face) => {
          face.setAttribute('aria-hidden', String(face.dataset.rvFace !== side));
        });

        this.dispatchEvent(new CustomEvent('rv:change', { detail: { side }, bubbles: true }));
      }
    }

    customElements.define('rv-flip', RvFlip);
  }

  /*
   * <rv-video> — wraps a muted, looping video and wires a pause/play button
   * ([data-rv-video-toggle], aria-pressed="true" while paused).
   * Only plays while on screen (saves battery and data on phones), never autoplays for
   * reduced motion, and a pause by the shopper sticks until they press play again.
   */
  if (!customElements.get('rv-video')) {
    class RvVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-rv-video-toggle]');
        if (!this.video || this._ready) return;
        this._ready = true;

        this.video.muted = true;
        this.video.playsInline = true;
        this.video.removeAttribute('autoplay');
        this.userPaused = reducedMotion();
        this.visible = false;
        if (this.userPaused) this.video.pause();

        this.video.addEventListener('play', () => this.sync());
        this.video.addEventListener('pause', () => this.sync());

        this.toggle?.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.visible = entry.isIntersecting;
              if (this.visible && !this.userPaused) this.play();
              if (!this.visible && !this.video.paused) this.video.pause();
            },
            { threshold: 0.25 }
          );
          this.observer.observe(this);
        } else if (!this.userPaused) {
          this.play();
        }

        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      play() {
        this.video.play().catch(() => this.sync());
      }

      sync() {
        if (!this.toggle) return;
        const paused = this.video.paused;
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.playLabel || 'Play video' : this.toggle.dataset.pauseLabel || 'Pause video');
      }
    }

    customElements.define('rv-video', RvVideo);
  }

  /*
   * "Watch the flip" links (a[href="#reversible-showcase"], e.g. in snippets/reversible-pdp-callout.liquid):
   * the page scrolls to the showcase as usual, and its video restarts from the first frame so the shopper
   * sees the whole flip. Skipped for reduced motion (the video stays paused there). One delegated listener,
   * added once even if this file is included by several sections.
   */
  if (!window.rvShowcaseLinkBound) {
    window.rvShowcaseLinkBound = true;

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('a[href="#reversible-showcase"]')) return;
      if (reducedMotion()) return;

      const player = document.querySelector('#reversible-showcase rv-video');
      if (!player || !player.video || typeof player.play !== 'function') return;

      player.userPaused = false;
      try {
        player.video.currentTime = 0;
      } catch (error) {
        // Metadata not loaded yet: it starts from 0 anyway.
      }
      player.play();
    });
  }
})();
